import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import MultiSelectDropdown from '../../dropdowns/MultiSelectDropdownNew';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import { toast } from 'sonner';
import { AddToCartConfigurationTabProps } from './types';

export default function AddToCartConfiguration(props: AddToCartConfigurationTabProps) {
  const {
    formData,
    updateFormData,
    availableForms,
    existingDomainSuggestions,
    isCharityStackInternalURL,
    handleFieldFocus,
    isFieldInvalid,
    invalidFields,
    setInvalidFields,
  } = props;

  const { elementName, connectedForms, allowedDomains, disallowedDomains, customShareLink } =
    formData;

  const setConnectedForms = (forms: string[]) => {
    updateFormData('connectedForms', forms);
  };

  const setDisallowedDomains = (forms: string[]) => {
    updateFormData('disallowedDomains', forms);
  };
  return (
    <>
      {/* Element Name */}
      <div className="space-y-0">
        <Label
          htmlFor="element-name"
          className="font-inter text-base font-medium leading-6 text-ink-secondary"
        >
          Element Name
        </Label>
        <Input
          id="element-name"
          value={elementName}
          onChange={(e) => updateFormData('elementName', e.target.value)}
          onFocus={() => handleFieldFocus('configuration-elementName')}
          className={`h-[28px] flex-shrink-0 self-stretch ${
            isFieldInvalid('elementName') ? 'border-red-500' : ''
          }`}
        />
      </div>

      {/* Connected Forms */}
      <div className="space-y-0">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Connected Forms
        </Label>
        <div>
          <MultiSelectDropdownNoCreate
            height={28}
            options={(availableForms || [])
              .sort((a, b) => a.formName.localeCompare(b.formName))
              .map((form) => ({
                value: form.formName,
                label: form.formName,
              }))}
            setItems={(items) => {
              setConnectedForms(items);
              handleFieldFocus('configuration-connectedForms');
            }}
            items={connectedForms}
            error={isFieldInvalid('connectedForms')}
          />
        </div>
      </div>

      {/* Domain Allow List */}
      <div className="space-y-1">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Domain Allow List
        </Label>
        <div>
          <MultiSelectDropdown
            options={[...existingDomainSuggestions.whitelist]
              .filter((v, i, a) => a.indexOf(v) === i)
              .filter((domain) => !isCharityStackInternalURL(domain))
              .map((domain) => ({ value: domain, label: domain }))}
            setItems={(items) => {
              // Filter out CharityStack internal URLs
              const filteredItems = items.filter((item) => !isCharityStackInternalURL(item));
              // Show error if user tried to add blocked domain
              if (filteredItems.length < items.length) {
                toast.error(
                  'Cannot add CharityStack internal URLs (donate, attend, dashboard pages) to whitelist',
                );
              }

              updateFormData('allowedDomains', filteredItems);
              handleFieldFocus('configuration-allowedDomains');
            }}
            items={allowedDomains}
            error={isFieldInvalid('allowedDomains')}
            errorMessage={isFieldInvalid('allowedDomains') ? 'At least one valid domain required' : undefined}
          />
        </div>
      </div>

      {/* Domain Disallow List */}
      <div className="space-y-1">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Domain Disallow List
        </Label>
        <MultiSelectDropdown
          height={28}
          options={[...existingDomainSuggestions.blacklist]
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((domain) => ({ value: domain, label: domain }))}
          setItems={setDisallowedDomains}
          items={disallowedDomains}
        />
      </div>

      {/* Custom Share Link */}
      <div className="space-y-0">
        <Label
          htmlFor="custom-share-link"
          className="font-inter text-base font-medium leading-6 text-ink-secondary"
        >
          Custom Share Link
        </Label>
        <Input
          id="custom-share-link"
          value={customShareLink}
          onChange={(e) => {
            updateFormData('customShareLink', e.target.value);
          }}
          onFocus={() => handleFieldFocus('configuration-customShareLink')}
          className={`h-[28px] flex-shrink-0 self-stretch ${
            isFieldInvalid('customShareLink') ? 'border-red-500' : ''
          }`}
          placeholder="example.com"
        />
        {isFieldInvalid('customShareLink') && (
          <p className="mt-1 text-xs text-red-500">Custom share link is invalid</p>
        )}
      </div>
    </>
  );
}
